/**
 * @module lib/crypto/crypto-utils
 */

const { Binary } = require('binary');
const { WritableStreamBuffer } = require('stream-buffers');

/**
 * Reads a stream.
 *
 * @param  {Binary} payload - The input payload
 * @return {Promise<Binary>}
 */
module.exports.readStream = function (payload) {
  return new Promise(function promise(resolve, reject) {
    const length = payload.endOffset - payload.start;
    const ostream = new WritableStreamBuffer({ initialSize: length });

    payload.pipe(ostream);

    ostream.on('finish', function onFinish() {
      const data = ostream.getContents();
      resolve(Binary.fromBuffer(data));
    });

    ostream.on('error', function onError(err) {
      reject(err);
    });
  });
};

/**
 * Validates a specified key size
 * @param  {Number} size in bits requested
 * @param  {Number} minSize in bits allowed
 * @return {Boolean}
 */
module.exports.isValidAsymmetricKeySize = function (size, minSize) {
  // No size specified is fine because the minSize will be used
  if (size === undefined) {
    return true;
  }

  if (typeof size !== 'number' || size < minSize) {
    return false;
  }

  return true;
};

/**
 * Format a base64 string representation of a key file
 * in PEM PKCS#8 format by adding a header and footer
 * and new lines.
 *
 * The PEM spec says to use <CR><LF> (\r\n) per
 * https://tools.ietf.org/html/rfc1421#section-4.3.2.2, but
 * many implementations use just \n, so this function
 * follows the convention over the spec.
 *
 * @param  {String} base64KeyString input
 * @param  {String} label header and footer label that identifies key type
 * @return {String} formatted output
 */
module.exports.formatAsPem = function (base64KeyString, label) {
  let pemCert = `-----BEGIN ${label}-----\n`;
  let nextIndex = 0;
  while (nextIndex < base64KeyString.length) {
    if (nextIndex + 64 <= base64KeyString.length) {
      pemCert += `${base64KeyString.substr(nextIndex, 64)}\n`;
    } else {
      pemCert += `${base64KeyString.substr(nextIndex)}\n`;
    }
    nextIndex += 64;
  }
  pemCert += `-----END ${label}-----\n`;
  return pemCert;
};

/**
 * Remove PEM formatting (new line characters and headers / footers)
 * from a PEM string
 *
 * @param  {String} input - PEM formatted string
 * @return {String}       - String with formatting removed
 */
module.exports.removePemFormatting = function (input) {
  let output = input.replace(/\n/g, '');
  output = output.replace(
    /-----(?:BEGIN|END)\s(?:RSA\s)?(?:PUBLIC|PRIVATE|CERTIFICATE)\sKEY-----/g,
    ''
  );
  return output;
};
